/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Apply the backend-authoritative interface language exactly once per mount.
 * @state-owner shared workspace language store, guarded by a per-mount ref
 * @allowed-imports React; shared workspace language store; @vispo/contracts types
 * @forbidden API client; module internals; role/permission inference
 * @public-api WorkspaceShell
 * @side-effects a one-shot write to the shared workspace language store
 * @decision Interface-language hydration applies the backend preference exactly once per mount via
 *   a ref guard (the M4 lesson: StrictMode double-invokes effects, and an un-guarded hydration would
 *   re-apply a since-superseded read on the second invocation). After that one application, only an
 *   explicit user action (SET-F03's language switch) may change the language for the rest of the
 *   session — a later background refetch of preferences must never silently overwrite it. Extracted
 *   from WorkspaceShell.tsx so this one-shot behavior can be unit-tested directly, with a fully
 *   controllable input, instead of only through the full route/query-timing stack.
 * @tests use-language-hydration.test.ts
 */

import type { PreferencesContext } from '@vispo/contracts';
import { useEffect, useRef } from 'react';
import { useWorkspaceUiStore } from '../shared/model/workspace-store';

export function useLanguageHydration(preferencesData: PreferencesContext | undefined) {
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !preferencesData) return;
    hydratedRef.current = true;
    useWorkspaceUiStore.getState().setLanguage(preferencesData.locale);
  }, [preferencesData]);
}
